import { MouseEvent, ReactNode } from 'react';

import { cx } from '@libs/classnames';
import { Card } from '@ui/components/Card';
import { IconButton } from '@ui/components/IconButton';
import { Popover } from '@ui/components/Popover';
import { ThemeLink } from '@ui/components/ThemeLink';
import { DeleteIcon } from '@ui/icons/DeleteIcon';
import { MenuBackArrow } from '@ui/icons/MenuBackArrow';

import styles from './PopoverMenu.module.css';

type PopoverMenuProps = {
  className?: string;
  containerClassName?: string;
  isOpen: boolean;
  onClose?: (e?: MouseEvent<HTMLElement>) => void;
  hideCloseButton?: boolean;
  children: ReactNode;
};
export function PopoverMenu({
  className,
  containerClassName,
  onClose,
  isOpen,
  hideCloseButton,
  children,
}: PopoverMenuProps) {
  return (
    <Popover className={className} onHide={onClose} active={isOpen}>
      <Card className={cx(styles.container, containerClassName)}>
        {!hideCloseButton && (
          <IconButton className={styles.closeButton} onClick={onClose} preset="transparent">
            <DeleteIcon />
          </IconButton>
        )}

        {children}
      </Card>
    </Popover>
  );
}

type PopoverMenuItemProps = {
  className?: string;
  onClick?: (e?: MouseEvent<HTMLElement>) => void;
  href?: string;
  inNewTab?: boolean;
  isSelected?: boolean;
  disabled?: boolean;
  children: ReactNode;
};
export function PopoverMenuItem({
  className,
  onClick,
  href,
  inNewTab,
  isSelected,
  disabled,
  children,
}: PopoverMenuItemProps) {
  return (
    <ThemeLink
      inNewTab={inNewTab}
      href={href}
      colorless
      className={cx(
        styles.menuItem,
        {
          [styles.clickable]: !!onClick || href,
          [styles.disabled]: disabled,
          [styles.selected]: isSelected,
        },
        className,
      )}
      onClick={onClick}
    >
      {children}
    </ThemeLink>
  );
}

export function PopoverMenuSeparator() {
  return <div className={styles.hr} />;
}

type PopoverSubMenuProps = {
  className?: string;
  title?: ReactNode;
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
};
export function PopoverSubMenu({
  className,
  title,
  isOpen,
  onClose,
  children,
}: PopoverSubMenuProps) {
  return (
    <Card className={cx(styles.subMenu, { [styles.open]: isOpen }, className)}>
      <div className={styles.title}>
        <IconButton className={styles.backButton} preset="transparent" onClick={onClose}>
          <MenuBackArrow />
        </IconButton>
        <div>{title}</div>
      </div>

      {children}
    </Card>
  );
}
